import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getAExistingControlbySecurityControl } from '../../redux/actions/actionActions'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'

import { DataGrid, GridColDef, GridCellParams } from '@mui/x-data-grid'

import { useTranslation } from 'react-i18next'
import { formatDecimal, formatPercent } from '../../utils/formatNumbers'
import { numericColumn } from '../../utils/dataGridStyles'
import '../../i18n'
import { useNavigate, useParams } from 'react-router-dom'
import DeleteButton from '../../components/common/DeleteButton'
import { deleteExistingControl } from '../../redux/existingControls/existingControlActions'
import DriveFileRenameOutlineOutlined from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import i18n from '../../i18n'
import { translatePerimeter } from '../../helpers/helper'
import { systemLabel } from '../../utils/systemLabel'
import { UserRole } from '../../types/keycloak'
import { hasRole } from '../../utils/checkRole'
import {
  ListPageShell,
  listPageDataGridSx,
  listPageGridSlots,
  listPageIconBtnStyle,
  listPageRowActionsStyle,
} from '../../components/common/ListPageShell'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
export default function ExistingControlListBySecurityCheckFamily() {
  const [search, setSearch] = React.useState('')
  const { t } = useTranslation()
  const existingControlsList = useSelector(
    (state: RootState) => state.action.existingControls
  )

  const dispatch: AppDispatch = useAppDispatch()

  const loading = useSelector((state: RootState) => state.action.loading)
  const navigate = useNavigate()

  const { SecurityId } = useParams<{ SecurityId: string }>()
  const showLoader = useTableLoadingGate(loading, [SecurityId])

  useEffect(() => {
    if (SecurityId) {
      dispatch(getAExistingControlbySecurityControl(SecurityId))
    }
  }, [dispatch, SecurityId])
  const handleDeleteClick = (existingControlId: string) => {
    dispatch(deleteExistingControl(existingControlId)).then(() => {
      dispatch(getAExistingControlbySecurityControl(SecurityId || ''))
    })
  }

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return existingControlsList
    return existingControlsList.filter((row: any) =>
      [row.Label, row.Description, row.ControlNature, row.Perimetre]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    )
  }, [existingControlsList, search])

  const columns: GridColDef[] = [
    {
      field: 'Label',
      headerName: 'Label',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <span
          className="incyia-name-link line-clamp-2 overflow-hidden text-ellipsis cursor-pointer"
          onClick={() =>
            navigate(
              `/security-controls/${SecurityId}/view-existing-Control/${params?.id}`
            )
          }
        >
          {params.value}
        </span>
      ),
    },
    {
      field: 'Description',
      headerName: 'Description',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          {params.value}
        </span>
      ),
    },
    {
      field: 'Perimetre',
      headerName: t('Perimeter'),
      minWidth: 100,
      flex: 1,
      valueGetter: (params) => {
        const lang = i18n.language || 'en'
        return params.row?.Perimetre
          ? translatePerimeter(params.row.Perimetre, lang)
          : ''
      },
      renderCell: (params) => {
        const lang = i18n.language || 'en'
        const value = params.row?.Perimetre
          ? translatePerimeter(params.row.Perimetre, lang)
          : ''
        return (
          <span className="line-clamp-2 overflow-hidden text-ellipsis">
            {value || t('No data')}
          </span>
        )
      },
    },
    {
      field: 'NombreActifCouvert',
      headerName: t('Number of Active Covered'),
      flex: 1,
      ...numericColumn(200),
      valueFormatter: ({ value }) => formatDecimal(value, { decimals: 0 }),
    },
    {
      field: 'TauxCouverture',
      headerName: t('Coverage Rate'),
      flex: 1,
      ...numericColumn(130),
      valueFormatter: ({ value }) => formatPercent(value),
    },
    {
      field: 'ControlNature',
      headerName: t('Control Nature'),
      minWidth: 100,
      flex: 1,
      renderCell: (params) => (
        <span className="line-clamp-2 overflow-hidden text-ellipsis">
          {params.value
            ? systemLabel(t, 'controlNature', String(params.value))
            : ''}
        </span>
      ),
    },

    {
      field: 'Action',
      headerName: 'Action',
      minWidth: 100,
      flex: 0.5,
      renderCell: (params: GridCellParams) => (
        <div className="list-row-actions" style={listPageRowActionsStyle}>
          <button
            type="button"
            aria-label={t('buttons.update')}
            style={listPageIconBtnStyle}
            onClick={() => navigate(`/existingControl/${params.id}`)}
          >
            <DriveFileRenameOutlineOutlined sx={{ fontSize: 18 }} />
          </button>
          <DeleteButton
            onDelete={() => handleDeleteClick(params.id as string)}
            item={{
              entityType: t('Existing Controls', {
                defaultValue: 'Existing control',
              }),
              name:
                params.row.Label ||
                params.row.ExistingControlName ||
                undefined,
            }}
          />
        </div>
      ),
    },
  ]

  const roles = useSelector((state: any) => state.auth.roles)
  const canManage =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)

  return (
    <div className="page-content">
      <ListPageShell
        eyebrow={t('securityControls')}
        title={t('Existing Controls List')}
        subtitle={t('existingControlsList.subtitle', {
          defaultValue:
            'Review the controls already implemented for this security requirement.',
        })}
        addLabel={canManage ? t('buttons.addExistingControl') : undefined}
        onAdd={canManage ? () => navigate(`/addexisting/${SecurityId}`) : undefined}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('filterByName')}
        countLabel={t('listPages.itemsCount', {
          count: filteredRows.length,
          defaultValue: `${filteredRows.length} items`,
        })}
      >
        <div className="w-full max-w-full overflow-hidden">
          <DataGrid
            loading={showLoader}
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row._id}
            hideFooter
            slots={listPageGridSlots}
            sx={listPageDataGridSx(filteredRows.length)}
          />
        </div>
      </ListPageShell>
    </div>
  )
}
